import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import { isEmpty } from '@ember/utils'
import { ThemeKeys, subscriptionTypeToThemeKey } from 'teacher-ui-client/utils/themes'
import { buildDataOperationsConfigProxy } from 'teacher-ui-client/components/data-operations/data-operations-proxy'
import { SortType, SortDirection } from 'teacher-ui-client/components/data-operations/table/sort-definition'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Teacher from 'teacher-ui-client/models/teacher'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { on } from '@ember/modifier'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import pick from 'teacher-ui-client/helpers/pick'
import InputText from 'teacher-ui-client/components/base/input-text'
import UiButton from 'teacher-ui-client/components/ui-button'
import DataOperationsTable from 'teacher-ui-client/components/data-operations/table'
import includes from 'teacher-ui-client/helpers/includes'
import themeKey from 'teacher-ui-client/helpers/theme-key'
import type Owner from '@ember/owner'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface Signature {
  Args: {
    subscriptionType: SubscriptionType
    teachers: Teacher[]
    addWithSubAction: (ids: string[]) => void
    addWithTrialAction: (ids: string[]) => void
    hideLogin?: boolean
  }
}

const themes = {
  [ThemeKeys.blue]: {
    controls: 'bg-oceany-blue-150',
    headCells: 'bg-oceany-blue-100',
    bodyRows: 'even:bg-oceany-blue-50 hover:bg-oceany-blue-100',
  },
  [ThemeKeys.green]: {
    controls: 'bg-ms-green-150',
    headCells: 'bg-ms-green-100',
    bodyRows: 'even:bg-ms-green-50 hover:bg-ms-green-100',
  },
  [ThemeKeys.darkBlue]: {
    controls: 'bg-wl-blue-100',
    headCells: 'bg-wl-blue-100',
    bodyRows: 'even:bg-wl-blue-50 hover:bg-wl-blue-100',
  },
  [ThemeKeys.default]: {
    controls: 'bg-dusty-black-100',
    headCells: 'bg-dusty-black-100',
    bodyRows: 'even:bg-dusty-black-50 hover:bg-dusty-black-100',
  },
}

export class AddTeachersDialog extends Component<Signature> {
  @service featureControl!: FeatureControlService

  @tracked sortCol = 'lastName'

  @tracked sortDir = SortDirection.Asc

  @tracked sortCompare = SortType.ByValue

  @tracked currentPage = 1

  @tracked recordsPerPage = 10

  @tracked pageCount = 1

  @tracked textFilter = ''

  @tracked selectedIds: string[] = []

  dataOperationsConfig: this

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args)
    this.dataOperationsConfig = buildDataOperationsConfigProxy(this)
  }

  get themeClasses() {
    const themeKey = subscriptionTypeToThemeKey(this.args.subscriptionType)
    return themes[themeKey]
  }

  get showAddTrialSubscriptionButton() {
    return this.featureControl.canAccess('create_trial_subscriptions')
  }

  get recordsFilteredByText() {
    const allTeachers = this.args.teachers.filter(hasId)
    const trimmedTextFilter = this.textFilter.trim()

    if (isEmpty(trimmedTextFilter)) return allTeachers

    const selectedIds = this.selectedIds || []

    return allTeachers.filter((teacher) => {
      // if it's selected, keep it even if it doesn't match the filter
      if (selectedIds.includes(teacher.id)) return true

      const content = [teacher.firstName, teacher.lastName, teacher.login].join(' ')

      return content.toLowerCase().includes(trimmedTextFilter.toLowerCase())
    })
  }

  // Contains the results of all filtering operations
  get data() {
    return this.recordsFilteredByText
  }

  @action
  addWithTrial() {
    const ids = this.selectedIds
    this.args.addWithTrialAction(ids)
    this.selectedIds = []
  }

  @action
  addWithSubscription() {
    const ids = this.selectedIds
    this.args.addWithSubAction(ids)
    this.selectedIds = []
  }

  @action
  updateTextFilter(value: string) {
    this.textFilter = value
    this.currentPage = 1
  }

  <template>
    <DataOperationsTable @dataOperationsConfig={{this.dataOperationsConfig}} as |selectRecords sorting pagination|>

      <div>
        <div class="flex justify-between gap-1 p-4 {{this.themeClasses.controls}}">
          {{#if this.showAddTrialSubscriptionButton}}
            <UiButton
              data-test-add-with-trial
              disabled={{selectRecords.noneSelected}}
              class="whitespace-nowrap"
              {{on "click" this.addWithTrial}}
            >
              {{t "manage.manageTeachers.addTeachers.withTrial"}}
            </UiButton>
          {{/if}}

          <UiButton
            data-test-add-with-school-sub
            disabled={{selectRecords.noneSelected}}
            class="whitespace-nowrap"
            {{on "click" this.addWithSubscription}}
          >
            {{t "manage.manageTeachers.addTeachers.withSubscription"}}
          </UiButton>

          <InputText
            type="search"
            role="searchbox"
            name="search-filter"
            class="min-w-0"
            value={{this.textFilter}}
            placeholder={{t "manage.manageClasses.students.filterStudents.placeholder"}}
            aria-label={{t "manage.manageClasses.students.filterStudents.label"}}
            @themeKey={{themeKey @subscriptionType}}
            @icon="search"
            @iconEnd={{true}}
            {{on "input" (pick "target.value" this.updateTextFilter)}}
          />
        </div>

        <table data-test-add-existing-teacher-table class="w-full">
          <thead>
            <tr class="text-dusty-black-300 divide-x divide-white font-bold">
              <th class="w-0 text-center align-top {{this.themeClasses.headCells}}">
                <label class="p-2 select-none">
                  {{t "manage.selectAllLabel"}}
                  <input
                    data-test-select-all
                    type="checkbox"
                    checked={{selectRecords.allSelected}}
                    {{on "input" selectRecords.toggleAll}}
                  />
                </label>
              </th>
              <th class="{{this.themeClasses.headCells}}">
                <button
                  type="button"
                  class="w-full px-2 py-4 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "firstName")}}
                >
                  {{t "manage.firstName"}}
                  <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                </button>
              </th>
              <th class="{{this.themeClasses.headCells}}">
                <button
                  type="button"
                  class="w-full px-2 py-4 text-left whitespace-nowrap"
                  {{on "click" (fn sorting.sort SortType.ByValue "lastName")}}
                >
                  {{t "manage.lastName"}}
                  <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                </button>
              </th>
              {{#unless @hideLogin}}
                <th data-test-login-header class="{{this.themeClasses.headCells}}">
                  <button
                    type="button"
                    class="w-full px-2 py-4 text-left whitespace-nowrap"
                    {{on "click" (fn sorting.sort SortType.ByValue "login")}}
                  >
                    {{t "manage.login"}}
                    <PhosphorIcon @icon="phosphor-caret-up-down-fill" />
                  </button>
                </th>
              {{/unless}}
            </tr>
          </thead>

          <tbody>
            {{#each pagination.currentPageRecords as |row|}}
              <tr class={{this.themeClasses.bodyRows}} data-test-row-teacher-id={{row.id}}>
                <td class="w-0 text-center">
                  <label class="p-2" aria-label={{t "manage.manageClasses.selectSingleRecordLabel" name=row.fullName}}>
                    <input
                      data-test-add-teacher-checkbox
                      type="checkbox"
                      checked={{includes selectRecords.selectedIds row.id}}
                      {{on "input" (fn selectRecords.toggle row.id)}}
                    />
                  </label>
                </td>
                <td class="p-2 break-all">{{row.firstName}}</td>
                <td class="p-2 break-all">{{row.lastName}}</td>
                {{#unless @hideLogin}}
                  <td class="p-2 break-all">{{row.login}}</td>
                {{/unless}}
              </tr>
            {{/each}}
          </tbody>
        </table>
      </div>
    </DataOperationsTable>
  </template>
}

export default AddTeachersDialog
